<template>
  <component
    :is="computedTag"
    :type="nativeType"
    :href="href"
    :to="to"
    :class="computedClasses"
    :disabled="computedDisabled"
    @click="handleClick"
  >
    <!-- Loading spinner (Bootstrap 4 compatible) -->
    <span
      v-if="loading"
      class="spinner-border spinner-border-sm mr-2"
      role="status"
      aria-hidden="true"
    >
    </span>
    <span v-if="loading" class="sr-only">Loading...</span>

    <slot />
  </component>
</template>

<script setup>
import { computed } from "vue"

const props = defineProps({
  // Bootstrap Vue BButton API compatibility
  variant: {
    type: String,
    default: 'primary',
    validator: val => [
      'primary', 'secondary', 'success', 'danger', 'warning',
      'info', 'light', 'dark', 'link', 'outline-primary',
      'outline-secondary', 'outline-success', 'outline-danger',
      'outline-warning', 'outline-info', 'outline-light',
      'outline-dark', 'themed'
    ].includes(val)
  },
  size: {
    type: String,
    validator: val => ['sm', 'lg'].includes(val)
  },
  block: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'button'
  },
  href: {
    type: String,
    default: null
  },
  to: {
    type: [String, Object],
    default: null
  },
  tag: {
    type: String,
    default: null
  },

  // Custom enhancements
  loading: {
    type: Boolean,
    default: false
  },
  pill: {
    type: Boolean,
    default: false
  },
  squared: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['click'])

// a link with href, a router-link (resolved by its global name) with to, a button otherwise
const computedTag = computed(() => {
  if (props.tag) return props.tag
  if (props.href) return 'a'
  if (props.to) return 'router-link'
  return 'button'
})

const nativeType = computed(() => {
  if (computedTag.value === 'button') {
    return props.type
  }
  return undefined
})

const computedDisabled = computed(() => props.disabled || props.loading)

const computedClasses = computed(() => {
  const classes = ['btn']

  // Handle variant (including outline variants and themed)
  // if (this.variant === 'themed') {
  //   // For themed variant, don't add btn-themed class automatically
  //   // The themed styling will come from custom CSS classes passed via class attribute
  //   // This maintains compatibility with existing themed button styles
  // } else if (this.variant.startsWith('outline-')) {
  //   classes.push(`btn-${this.variant}`)
  // } else {
  //   classes.push(`btn-${this.variant}`)
  // }
  classes.push(`btn-${props.variant}`)

  // Size classes
  if (props.size) {
    classes.push(`btn-${props.size}`)
  }

  // Block button (Bootstrap 4)
  if (props.block) {
    classes.push('btn-block')
  }

  // Custom shape classes
  if (props.pill) {
    classes.push('rounded-pill')
  }
  if (props.squared) {
    classes.push('rounded-0')
  }

  // Loading state
  if (props.loading) {
    classes.push('disabled')
  }

  return classes
})

const handleClick = (event) => {
  if (!computedDisabled.value) {
    emit('click', event)
  } else {
    event.preventDefault()
    event.stopPropagation()
  }
}
</script>

<style scoped>
.btn.disabled {
  pointer-events: none;
  opacity: 0.6;
}

.btn .spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Future Bootstrap 5 compatibility classes */
.btn-block {
  display: block;
  width: 100%;
}

/* Custom pill and squared variants */
.rounded-pill {
  border-radius: 50rem !important;
}

.rounded-0 {
  border-radius: 0 !important;
}
</style>
